import { useHead } from '@unhead/vue'
import {
    computed,
    watch,
    ref,
    provide,
    inject,
    type Ref,
    readonly,
    onMounted,
    onUnmounted
} from 'vue'
import { useI18n } from 'vue-i18n'

const HCAPTCHA_INJECTION_KEY = 'HCAPTCHA_INJECTION_KEY'

declare global {
    interface Window {
        RedpointHcaptchaLoad?: () => void
        RedpointHcaptchaReady?: boolean
        RedpointHcaptchaReadyFunction?: () => void
        hcaptcha?: {
            render: (
                container: string | HTMLElement,
                parameters: Record<string, unknown>
            ) => number
            execute: (widgetId: number) => void
            reset: (widgetId: number) => void
        }
    }
}

export function useHcaptchaReady(): Readonly<Ref<boolean>> {
    return inject(HCAPTCHA_INJECTION_KEY, ref(false))
}

export function createHcaptcha() {
    const { locale } = useI18n({ useScope: 'global' })
    // Keep hCaptcha locale mapping explicit so unsupported storefront locale
    // keys do not leak into the third-party script URL.
    const lang = computed(() => {
        if (locale.value.startsWith('es-')) {
            return 'es'
        }

        if (locale.value.startsWith('fr-')) {
            return 'fr'
        }

        if (locale.value.startsWith('de-')) {
            return 'de'
        }

        return undefined
    })

    const url = computed(() => {
        return (
            'https://js.hcaptcha.com/1/api.js?onload=RedpointHcaptchaLoad&render=explicit' +
            // Keep hCaptcha from registering the reCAPTCHA-compatible grecaptcha
            // global, otherwise visible Google reCAPTCHA widgets can render hCaptcha.
            '&recaptchacompat=off' +
            (lang.value !== undefined ? '&hl=' + lang.value : '')
        )
    })

    const ready = ref(false)

    function handleRedpointHcaptchaLoad() {
        // hCaptcha calls this named global after api.js loads; define it in
        // bundled JavaScript so CSP does not need to allow inline callbacks.
        window.RedpointHcaptchaReady = true
        ready.value = true

        if (typeof window.RedpointHcaptchaReadyFunction === 'function') {
            window.RedpointHcaptchaReadyFunction()
        }
    }

    if (process.client) {
        window.RedpointHcaptchaLoad = handleRedpointHcaptchaLoad
    }

    watch(
        url,
        () => {
            ready.value = false
            // Locale changes need a fresh script readiness signal before any
            // component tries to render an hCaptcha widget.
            if (process.client) {
                window.RedpointHcaptchaReady = false
            }
        },
        { flush: 'sync' }
    )

    provide(HCAPTCHA_INJECTION_KEY, readonly(ready))

    onMounted(() => {
        if (window.RedpointHcaptchaReady) {
            ready.value = true
        }
        window.RedpointHcaptchaReadyFunction = () => {
            ready.value = true
        }
    })

    onUnmounted(() => {
        window.RedpointHcaptchaReadyFunction = undefined
    })

    if (process.client) {
        useHead({
            script: () => [
                {
                    src: url.value,
                    async: true,
                    defer: true
                }
            ]
        })
    }
}
